/* eslint-disable max-lines */
import React from 'react';
import {
  AccountTreeSharp,
  BarChartOutlined,
  CalendarViewDaySharp,
  DirectionsRunSharp,
  DonutLargeOutlined,
  DoubleArrowSharp,
  Filter2Sharp,
  GroupWork,
  HistorySharp,
  HouseSharp,
  PlaceSharp,
  PeopleAltSharp,
  Person,
  SettingsOutlined,
  SportsSoccer,
  StorageOutlined,
  TransferWithinAStation,
  BedroomBaby as BedroomBabyIcon,
  AccountBox,
  PendingActions,
  Build,
  Groups as GroupsIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

import { AsyncAutocomplete } from 'components';
import strings, { url } from 'constants/index';
import useAuth from 'contexts/AuthContext';
import { SCHEDULER_VIEWER_SCOPE } from 'features/scheduler';
import { useSports } from 'contexts/SportContext';
import { useTheme } from 'contexts/ThemeContext';
import { useSportRedirect } from 'hooks/common';
import { useActiveMenu } from 'hooks/context/useActiveMenu';
import { navigateWithStoredTableState, getStorageKeyForRoute } from 'utils/navigation.utils';

import { useAppDrawerStyles, appDrawerStyles } from './AppDrawer.styles';
import { Drawer } from 'components/Drawer';
import { Menu } from 'components/Menu';

export interface AppDrawerProps {
  isOpen: boolean;
  onOpenAction: () => void;
  setIsOpen: any;
}

const AppDrawer = ({ isOpen, onOpenAction, setIsOpen }: AppDrawerProps) => {
  const { sportId, sports, setCurrentSport } = useSports();
  const { user } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const { themeMode } = useTheme();
  const classes = useAppDrawerStyles({ themeMode });
  const [activeMenu] = useActiveMenu();
  // Redirecter to root section in case the sportId changes
  useSportRedirect();

  let items = [];
  const menuHandler = item => {
    const route = item.route || '';
    const storageKey = getStorageKeyForRoute(route);
    navigateWithStoredTableState(navigate, route, storageKey);
  };
  const populateSidebarItems = () => {
    if (activeMenu === 'competition-hierarchy') {
      items = [
        { subHeader: true, text: strings.fixtures },
        {
          route: `competition-hierarchy${url.sports}`,
          text: strings.sports,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/sports/.test(location.pathname),
          tab: 1,
          icon: <SportsSoccer />,
        },
        {
          route: `competition-hierarchy${url.organizations}`,
          text: strings.organizations,
          callback: menuHandler,
          selected: location.pathname.startsWith('/competition-hierarchy/organizations'),
          tab: 1,
          icon: <GroupWork />,
        },
        {
          route: `competition-hierarchy${url.competitions}`,
          text: strings.competitions,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/competitions/.test(location.pathname),
          tab: 1,
          icon: <DoubleArrowSharp />,
        },
        {
          route: `competition-hierarchy${url.seasons}`,
          text: strings.seasons,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/seasons/.test(location.pathname),
          tab: 1,
          icon: <CalendarViewDaySharp />,
        },
        {
          route: `competition-hierarchy${url.rounds}`,
          text: strings.rounds,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/rounds/.test(location.pathname),
          tab: 1,
          icon: <DonutLargeOutlined />,
        },
        {
          route: `competition-hierarchy${'/groups'}`,
          text: strings.groups,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/groups/.test(location.pathname),
          tab: 1,
          icon: <GroupsIcon />,
        },
        {
          route: `competition-hierarchy${url.fixtures}`,
          text: strings.fixtures,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/fixtures/.test(location.pathname),
          tab: 1,
          icon: <AccountTreeSharp />,
        },
        {
          route: `competition-hierarchy${url.venues}`,
          text: strings.venues,
          callback: menuHandler,
          selected: location.pathname.startsWith('/competition-hierarchy/venues'),
          tab: 1,
          icon: <HouseSharp />,
        },
        {
          route: `competition-hierarchy${url.localities}`,
          text: strings.localities,
          callback: menuHandler,
          selected: location.pathname.startsWith('/competition-hierarchy/localities'),
          tab: 1,
          icon: <PlaceSharp />,
        },
        { subHeader: true, text: strings.persons },
        {
          route: `competition-hierarchy${url.persons}`,
          text: strings.persons,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/persons/.test(location.pathname),
          tab: 1,
          icon: <Person />,
        },
        { subHeader: true, text: strings.competitors },
        {
          route: `competition-hierarchy${'/clubs'}`,
          text: strings.clubs,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/clubs/.test(location.pathname),
          tab: 1,
          icon: <GroupWork />,
        },
        {
          route: `competition-hierarchy${url.teams}`,
          text: strings.teams,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/competitors\/teams/.test(location.pathname),
          tab: 1,
          icon: <PeopleAltSharp />,
        },
        {
          route: `competition-hierarchy${url.players}`,
          text: strings.players,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/competitors\/players/.test(location.pathname),
          tab: 1,
          icon: <DirectionsRunSharp />,
        },
        {
          route: `competition-hierarchy${url.doubles}`,
          text: strings.doubles,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/competitors\/doubles/.test(location.pathname),
          tab: 1,
          icon: <Filter2Sharp />,
        },
        {
          route: `competition-hierarchy${url.horses}`,
          text: strings.horses,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/competitors\/horses/.test(location.pathname),
          tab: 1,
          icon: <BedroomBabyIcon />,
        },
        {
          route: `competition-hierarchy${url.tbds}`,
          text: strings.tbds,
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/competitors\/tbds/.test(location.pathname),
          tab: 1,
          icon: <TransferWithinAStation />,
        },
        { subHeader: true, text: 'Tools' },
        {
          route: 'competition-hierarchy/merge',
          text: 'Merge',
          callback: menuHandler,
          selected: /^\/competition-hierarchy\/merge/.test(location.pathname),
          tab: 1,
          icon: <Build />,
        },
      ];
    }
    if (activeMenu === 'proposals') {
      items = [
        { subHeader: true, text: strings.ingestion },
        {
          route: `proposals/proposal/ingestion${url.fixtures}`,
          text: strings.fixtures,
          callback: menuHandler,
          selected: location.pathname === '/proposals/proposal/ingestion/fixtures',
          tab: 1,
          icon: <StorageOutlined />,
        },
        {
          route: 'proposals/proposal/ingestion/squads',
          text: strings.squads,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/ingestion/squads'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        // {
        //   route: 'proposals/proposal/ingestion/fixturesquads',
        //   text: 'Fixture Squads',
        //   callback: menuHandler,
        //   selected: location.pathname.startsWith('/proposals/proposal/ingestion/fixturesquads'),
        //   tab: 1,
        //   icon: <StorageOutlined />,
        // },
        {
          route: 'proposals/proposal/ingestion/competitions',
          text: 'Competitions',
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/ingestion/competitions'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        { subHeader: true, text: strings.create_curation },
        {
          route: `proposals/proposal${url.create}${url.fixtures}`,
          text: strings.fixtures_create_proposals,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/create/fixtures'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        {
          route: `proposals/proposal${url.create}/players`,
          text: strings.players,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/create/players'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        {
          route: `proposals/proposal${url.create}/doubles`,
          text: strings.doubles,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/create/doubles'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        {
          route: `proposals/proposal${url.create}/teams`,
          text: strings.teams,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/create/teams'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        {
          route: `proposals/proposal${url.create}/contracts`,
          text: strings.squads,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/create/contracts'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        {
          route: `proposals/proposal${url.create}/hierarchy`,
          text: strings.hierarchy,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/create/hierarchy'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        { subHeader: true, text: strings.curation },
        {
          route: `proposals/proposal${url.update}`,
          text: strings.update_proposals,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/update'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        {
          route: `proposals/proposal${url.delete}`,
          text: strings.delete_proposals,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposal/delete'),
          tab: 1,
          icon: <StorageOutlined />,
        },
        { subHeader: true, text: strings.changes_log_subheader },
        {
          route: `proposals${url.proposed_changes}`,
          text: strings.proposed_changes,
          callback: menuHandler,
          selected: location.pathname.startsWith('/proposals/proposedchanges'),
          tab: 1,
          icon: <HistorySharp />,
        },
        {
          route: `proposals${url.changes_log}`,
          text: strings.changes_log,
          callback: menuHandler,
          selected: location.pathname.startsWith(url.base_changes_log),
          tab: 1,
          icon: <HistorySharp />,
        },
        {
          route: `proposals${url.dynamic_cadence}`,
          text: strings.dynamic_cadence,
          callback: menuHandler,
          selected: location.pathname.startsWith(url.base_dynamic_cadence),
          tab: 1,
          icon: <ScheduleIcon />,
        },
      ];
    }
    if (activeMenu === 'settings') {
      items = [
        { subHeader: true, text: strings.settings },
        {
          route: `settings${url.users}`,
          text: strings.users,
          callback: menuHandler,
          selected: location.pathname.startsWith('/settings/users'),
          tab: 1,
          icon: <SettingsOutlined />,
        },
        {
          route: `settings${url.sources}`,
          text: strings.manage_resources,
          callback: menuHandler,
          selected: location.pathname.startsWith('/settings/sources'),
          tab: 1,
          icon: <BarChartOutlined />,
        },
        {
          route: 'settings/ingestion-sources',
          text: strings.ingestion_sources,
          callback: menuHandler,
          selected: location.pathname.startsWith('/settings/ingestion-sources'),
          tab: 1,
          icon: <AccountBox />,
        },
        {
          route: `settings${url.system_info}`,
          text: strings.system_info,
          callback: menuHandler,
          selected: location.pathname.startsWith('/settings/systeminfo'),
          tab: 1,
          icon: <InfoIcon />,
        },
        ...(user?.scope?.includes(SCHEDULER_VIEWER_SCOPE)
          ? [
              {
                route: url.base_scheduler.slice(1),
                text: strings.scheduler,
                callback: menuHandler,
                selected: location.pathname.startsWith(url.base_scheduler),
                tab: 1,
                icon: <ScheduleIcon />,
              },
            ]
          : []),
        { subHeader: true, text: strings.matching_settings },
        {
          route: 'settings/matching-sources',
          text: strings.matching_sources,
          callback: menuHandler,
          selected: location.pathname.startsWith('/settings/matching-sources'),
          tab: 1,
          icon: <AccountBox />,
        },
      ];
    }
    if (activeMenu === 'matching') {
      items = [
        { subHeader: true, text: strings.pending_match_requests },
        {
          route: 'matching/pendingmatchrequests/fixtures',
          text: strings.fixtures,
          callback: menuHandler,
          selected: location.pathname.startsWith(`${url.base_pending_match_requests}/fixtures`),
          tab: 1,
          icon: <AccountTreeSharp />,
        },
        {
          route: 'matching/pendingmatchrequests/teams',
          text: strings.teams,
          callback: menuHandler,
          selected: location.pathname.startsWith(`${url.base_pending_match_requests}/teams`),
          tab: 1,
          icon: <PeopleAltSharp />,
        },
        {
          route: 'matching/pendingmatchrequests/players',
          text: strings.players,
          callback: menuHandler,
          selected: location.pathname.startsWith(`${url.base_pending_match_requests}/players`),
          tab: 1,
          icon: <DirectionsRunSharp />,
        },
        {
          route: 'matching/pendingmatchrequests/other',
          text: strings.other,
          callback: menuHandler,
          selected: location.pathname.startsWith(`${url.base_pending_match_requests}/other`),
          tab: 1,
          icon: <PendingActions />,
        },
        { subHeader: true, text: strings.mappings },
        {
          route: 'matching/sports',
          text: strings.sports,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/sports'),
          tab: 1,
          icon: <SportsSoccer />,
        },
        {
          route: 'matching/organizations',
          text: strings.organizations,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/organizations'),
          tab: 1,
          icon: <GroupWork />,
        },
        {
          route: 'matching/competitions',
          text: strings.competitions,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/competitions'),
          tab: 1,
          icon: <DoubleArrowSharp />,
        },
        {
          route: 'matching/seasons',
          text: strings.seasons,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/seasons'),
          tab: 1,
          icon: <CalendarViewDaySharp />,
        },
        {
          route: 'matching/rounds',
          text: strings.rounds,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/rounds'),
          tab: 1,
          icon: <DonutLargeOutlined />,
        },
        {
          route: 'matching/fixtures',
          text: strings.fixtures,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/fixtures'),
          tab: 1,
          icon: <AccountTreeSharp />,
        },
        {
          route: 'matching/venues',
          text: strings.venues,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/venues'),
          tab: 1,
          icon: <HouseSharp />,
        },
        {
          route: 'matching/localities',
          text: strings.localities,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/localities'),
          tab: 1,
          icon: <PlaceSharp />,
        },
        { subHeader: true, text: strings.persons },
        {
          route: 'matching/persons',
          text: strings.persons,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/persons'),
          tab: 1,
          icon: <Person />,
        },
        { subHeader: true, text: strings.competitors },
        {
          route: 'matching/clubs',
          text: strings.clubs,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/clubs'),
          tab: 1,
          icon: <GroupWork />,
        },
        {
          route: 'matching/conferences',
          text: strings.conferences,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/conferences'),
          tab: 1,
          icon: <AccountTreeSharp />,
        },
        {
          route: 'matching/divisions',
          text: strings.divisions,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/divisions'),
          tab: 1,
          icon: <Filter2Sharp />,
        },
        {
          route: 'matching/competitors/teams',
          text: strings.teams,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/competitors/teams'),
          tab: 1,
          icon: <PeopleAltSharp />,
        },
        {
          route: 'matching/competitors/players',
          text: strings.players,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/competitors/players'),
          tab: 1,
          icon: <DirectionsRunSharp />,
        },
        {
          route: 'matching/competitors/doubles',
          text: strings.doubles,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/competitors/doubles'),
          tab: 1,
          icon: <Filter2Sharp />,
        },
        {
          route: 'matching/competitors/horses',
          text: strings.horses,
          callback: menuHandler,
          selected: location.pathname.startsWith('/matching/competitors/horses'),
          tab: 1,
          icon: <BedroomBabyIcon />,
        },
        { subHeader: true, text: strings.logs },
        {
          route: 'matching/changesLog',
          text: strings.changes_log,
          callback: menuHandler,
          selected: location.pathname.startsWith(url.base_matching_changes_log),
          tab: 1,
          icon: <HistorySharp />,
        },
      ];
    }
  };
  populateSidebarItems();
  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onResizeEnd={(width, open) => (isOpen === false ? setIsOpen(open) : null)}
      // onResizeEnd={(width, open) => setIsOpen(open)}
      toggleOpen={onOpenAction}
      // onre
      width={232}
      customClasses={{
        root: classes.root,
        // button: classes.drawerButton,
      }}
      {...({
        children: (
          <Box paddingLeft="8px" paddingRight="13px" maxHeight="100%" overflow="hidden auto">
            <AsyncAutocomplete
              id="sport-autocomplete"
              label={strings.choose_sport}
              sx={appDrawerStyles.sportSelect}
              dataSource={sports}
              defaultValue={sportId ? sports.find(el => el.id === sportId) : null}
              onChange={setCurrentSport}
              textFieldProps={{ 'data-testid': 'sport-autocomplete' }}
              disabled={activeMenu === 'matching'}
            />
            <Menu items={items} layout="side" />
          </Box>
        ),
      } as { children: React.ReactNode })}
    />
  );
};

export default AppDrawer;
